import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { EVENT_NAME, PAGE_NAMES } from 'appenv';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Typography from '@material-ui/core/Typography';
import Breadcrumbs from '@material-ui/core/Breadcrumbs';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  selectAllLiveWebinars,
  selectAllOnDemandWebinars,
  selectIsWebinarsLoading,
} from 'models/webinars';
import Routes from 'Routes';
import BoothLightboxContextProvider, { BoothLightboxContext } from 'pages/booths/components/BoothLightboxContext';

import VideoLightbox from 'pages/booths/components/CustomLightbox/VideoLightbox';
import XtraAppBar from '../../components/appbar/XtraAppBar';
import Footer from '../../components/Footer';
import LiveListContainer from './LiveListContainer';
import OnDemandListContainer from './OnDemandListContainer';

const RootContainerUI = styled.div`
  height: 100%;
  overflow: hidden;

  @media (min-width: 960px) {
    background-color: #FFF;
  }

  @media (max-width: 960px) {
    background-color: #F0F1F2;
  }
`;

function RootContainer({ children }) {
  return (
    <RootContainerUI>
      {children}
    </RootContainerUI>
  );
}

RootContainer.propTypes = {
  children: PropTypes.node.isRequired,
};

const PageWrapper = styled.div`
  position: relative;
  min-width: 100%;
  height: 100%;
  overflow: auto;
  flex-direction: column;
  justify-content: space-between;
  display: flex;
  -webkit-overflow-scrolling: touch;

  @media (min-width: 960px) {
    height: calc(100% - 67.73px);
  }

  @media (max-width: 959px) {
    padding-top: 56px;
  }
`;

const PageContentContainer = styled.div`
  flex: 1 0 auto;
`;

const BreadcrumbsContainer = styled.div`
  display: flex;
  align-items: center;

  @media (min-width: 960px) {
    position: absolute;
    z-index: 1000;
    padding: 24px;
  }

  @media (max-width: 960px) {
    width: 100%;
    background-color: #FFF;
    padding: 16px;
  }
`;

const StyledLink = styled(Link)`
  text-decoration: none;
`;

const StyledTabsContainer = withStyles(() => ({
  root: {
    '@media (min-width: 960px)': {
      paddingTop: '16px',
    },
    '@media (max-width: 960px)': {
      backgroundColor: '#FFF',
    },
  },
  flexContainer: {
    borderBottom: '2px solid #DBDEE2',
  },
  indicator: {
    backgroundColor: '#0CA58B',
    height: '4px',
  },
}))(Tabs);

const StyledTab = withStyles(() => ({
  root: {
    color: '#A2AAB3',
    fontSize: '18px',
    fontWeight: 'bold',
    textTransform: 'none',
  },
  selected: {
    color: '#0CA58B',
  },
}))(Tab);

const WebinarPageContainer = () => {
  const [activeTab, setActiveTab] = useState(0);
  const liveWebinars = useSelector(selectAllLiveWebinars());
  const onDemandWebinars = useSelector(selectAllOnDemandWebinars());
  const isLoading = useSelector(selectIsWebinarsLoading);

  const onTabClick = (_, selectedTab) => {
    setActiveTab(selectedTab);
  };

  const tabs = {
    0: (<LiveListContainer webinars={liveWebinars} isLoading={isLoading} />),
    1: (<OnDemandListContainer webinars={onDemandWebinars} isLoading={isLoading} />),
  };

  return (
    <RootContainer>
      <XtraAppBar />
      <Helmet>
        <meta charSet="utf-8" />
        <title>{`${PAGE_NAMES.webinar} | ${EVENT_NAME}`}</title>
        <meta name="description" content={`${PAGE_NAMES.webinar} page of ${EVENT_NAME}.`} />
      </Helmet>
      <PageWrapper>
        <BoothLightboxContextProvider>
          <PageContentContainer>
            <BreadcrumbsContainer>
              <Breadcrumbs aria-label="breadcrumb" separator=">">
                <StyledLink to={Routes.lobby}>
                  Lobby
                </StyledLink>
                <Typography color="textPrimary">
                  Stage Programme
                </Typography>
              </Breadcrumbs>
            </BreadcrumbsContainer>
            <StyledTabsContainer
              value={activeTab}
              onChange={onTabClick}
              centered
            >
              <StyledTab label="Plenary Sessions" />
              <StyledTab label="Tech Ventures Pitching" />
            </StyledTabsContainer>
            {tabs[activeTab]}
          </PageContentContainer>
          <BoothLightboxContext.Consumer>
            {(videoContext) => !!(videoContext.cfStreamId || videoContext.url) && (
              <VideoLightbox
                open={!!(videoContext.cfStreamId || videoContext.url)}
                src={videoContext.url}
                cfStreamId={videoContext.cfStreamId}
                onClose={videoContext.onClose}
              />
            )}
          </BoothLightboxContext.Consumer>
        </BoothLightboxContextProvider>
        <Footer hashState="/webinar" />
      </PageWrapper>
    </RootContainer>
  );
};

export default WebinarPageContainer;
